/**
 * The System page's two feeds, straight from pv-auth:
 *   GET /provider/admin/ops/health   latest watchtower report + history + dead-man
 *   GET /provider/admin/ops/events   Sentry, Stripe, webhook log, PostHog, watchtower findings
 * Shapes mirror src/services/ops/opsAdmin.service.ts in pv-auth-service.
 */

export type OpsCheckStatus = 'ok' | 'degraded' | 'critical' | 'skipped';

export interface OpsCheck {
  name: string;
  group: string;
  status: OpsCheckStatus;
  detail: string;
  fix: string;
  remediated: boolean;
  remediation?: string;
  remediationFailed?: boolean;
  firstSeenAt?: string;
  lastNotifiedAt?: string;
}

export interface OpsReport {
  runId: string;
  runUrl?: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  overall: 'ok' | 'degraded' | 'critical';
  checks: OpsCheck[];
  secretsMissing?: string[];
  alerts?: { pushover: { kind: string; ok: boolean }[]; email: { kind: string; ok: boolean }[]; slack: { kind: string; ok: boolean }[] };
  redeployDispatched?: boolean;
}

export interface OpsHistoryRow {
  id: string;
  overall: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  critical: string[];
  degraded: string[];
  remediated: string[];
  runUrl: string | null;
}

export interface OpsHealth {
  latest: OpsReport | null;
  history: OpsHistoryRow[];
  deadMan: { lastRunAt: string | null; ageMinutes: number | null; silent: boolean; intervalMinutes: number; silentAfterMinutes: number; alarm: string; heartbeatAlarm: string };
  window: { runs: number; critical: number; degraded: number; remediations: number; hours: number };
}

export type OpsEventSource = 'sentry' | 'stripe' | 'revenuecat' | 'posthog' | 'watchtower';
export interface OpsEvent { at: string; source: OpsEventSource; kind: string; title: string; detail: string; level: 'info' | 'warn' | 'critical'; url: string | null }
export interface OpsSourceStatus { configured: boolean; ok: boolean; note: string }
export interface OpsEvents {
  events: OpsEvent[];
  sources: Record<OpsEventSource, OpsSourceStatus>;
  posthogHourly: { hour: string; count: number }[] | null;
  generatedAt: string;
}

export async function fetchOpsHealth(): Promise<OpsHealth> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/ops/health?limit=60&hours=24', true)) as OpsHealth;
}

export async function fetchOpsEvents(): Promise<OpsEvents> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/ops/events?limit=50&hours=24', true)) as OpsEvents;
}

/** Group order and labels for the tile board. */
export const GROUPS: { key: string; label: string }[] = [
  { key: 'http', label: 'Public surfaces' },
  { key: 'auth', label: 'Auth service dependencies' },
  { key: 'box', label: 'Production box' },
  { key: 'cloud', label: 'CloudWatch' },
  { key: 'vendors', label: 'Vendors' },
];

export const CHECK_LABELS: Record<string, string> = {
  api_health: 'API', ai_inference: 'AI inference', elina_llm: 'Elina', app_version: 'App version policy', pricing: 'Pricing',
  web: 'www.posture.vision', admin_web: 'admin.posture.vision', provider_web: 'provider.posture.vision',
  auth_db: 'Postgres', auth_redis: 'Redis', auth_sendgrid: 'SendGrid key', auth_ghl: 'GHL API', auth_apple: 'Sign in with Apple', auth_ghl_outbox: 'GHL outbox', auth_deep: 'Auth deep health',
  compose_services: 'Compose services', root_disk: 'Root disk', auth_image_current: 'Auth image = ECR',
  cloudwatch_eu_central_1: 'Alarms, Frankfurt', cloudwatch_us_east_1: 'Alarms, prod-us',
  sentry: 'Sentry', stripe_webhooks: 'Stripe webhooks', revenuecat: 'RevenueCat', posthog: 'PostHog',
};

export const checkLabel = (name: string): string => CHECK_LABELS[name] ?? name.replace(/_/g, ' ');

/** Which checks to put in the board, grouped, in a stable order. */
export function groupChecks(checks: OpsCheck[]): { key: string; label: string; checks: OpsCheck[] }[] {
  const known = new Set(GROUPS.map((g) => g.key));
  const out = GROUPS.map((g) => ({ ...g, checks: checks.filter((c) => c.group === g.key) }));
  const other = checks.filter((c) => !known.has(c.group));
  if (other.length) out.push({ key: 'other', label: 'Other', checks: other });
  return out.filter((g) => g.checks.length > 0);
}

/** The one line the top of the page says. */
export function headline(h: OpsHealth | undefined): { tone: OpsCheckStatus; text: string } {
  if (!h) return { tone: 'skipped', text: 'Reading the watchtower' };
  if (h.deadMan.silent) {
    return { tone: 'critical', text: h.deadMan.lastRunAt ? `The watchtower has been silent for ${h.deadMan.ageMinutes} minutes (runs every ${h.deadMan.intervalMinutes}). Its CloudWatch alarm pages Pushover on its own.` : 'No watchtower report has ever arrived.' };
  }
  const r = h.latest;
  if (!r) return { tone: 'skipped', text: 'No report stored yet.' };
  const crit = r.checks.filter((c) => c.status === 'critical');
  const deg = r.checks.filter((c) => c.status === 'degraded');
  const rem = r.checks.filter((c) => c.remediated);
  if (crit.length) return { tone: 'critical', text: `${crit.length} check${crit.length > 1 ? 's' : ''} need${crit.length > 1 ? '' : 's'} a human: ${crit.map((c) => checkLabel(c.name)).join(', ')}.` };
  if (deg.length) return { tone: 'degraded', text: `Everything is up. ${deg.length} degraded: ${deg.map((c) => checkLabel(c.name)).join(', ')}.` };
  if (rem.length) return { tone: 'ok', text: `Everything is up. Fixed automatically this run: ${rem.map((c) => checkLabel(c.name)).join(', ')}.` };
  return { tone: 'ok', text: 'Everything is up and nothing needed fixing.' };
}
